/**
 * Background log capture for "Report a problem" (Bill 2026-10-07).
 *
 * Installed once at app start. Keeps three small rolling buffers in memory -
 * console messages, uncaught errors, and network calls - so a report carries
 * what happened BEFORE the lifebuoy was clicked without the user having to
 * reproduce anything. Nothing leaves the browser until the user presses Send.
 *
 * Redaction is applied when an entry is recorded, not when the report is
 * built: tax-ID-shaped and card-length digit runs are masked, query strings
 * are dropped from URLs (that is where tax IDs travel), and request/response
 * BODIES are never recorded at all - only method, path, status and timing.
 */

export interface ConsoleEntry { at: string; level: 'error' | 'warn' | 'info' | 'log'; message: string }
export interface ErrorEntry { at: string; message: string; source?: string; stack?: string }
export interface NetworkEntry { at: string; method: string; path: string; status: number | null; ms: number; requestId?: string; failed?: string }

const MAX_CONSOLE = 150;
const MAX_ERRORS = 50;
const MAX_NETWORK = 150;
const MAX_TEXT = 600;

const consoleLog: ConsoleEntry[] = [];
const errorLog: ErrorEntry[] = [];
const networkLog: NetworkEntry[] = [];

/** Mask dashed and undashed 9-digit tax IDs, and 12-19 digit runs (card / account numbers, last 4 kept). */
export function redactText(input: string): string {
  return input
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****')
    .replace(/\b\d{12,19}\b/g, (m) => '•'.repeat(m.length - 4) + m.slice(-4))
    .replace(/\b\d{9}\b/g, '*********');
}

function clip(s: string): string {
  const r = redactText(s);
  return r.length > MAX_TEXT ? r.slice(0, MAX_TEXT) + '…' : r;
}

function push<T>(buf: T[], max: number, entry: T): void {
  buf.push(entry);
  if (buf.length > max) buf.splice(0, buf.length - max);
}

function stringify(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try { return JSON.stringify(arg); } catch { return String(arg); }
}

function pathOnly(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin ? u.pathname : `${u.host}${u.pathname}`;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

let installed = false;

/** Idempotent. Call once at app start (main.tsx) so the buffers fill from the first screen. */
export function installSupportCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  for (const level of ['error', 'warn', 'info', 'log'] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try { push(consoleLog, MAX_CONSOLE, { at: new Date().toISOString(), level, message: clip(args.map(stringify).join(' ')) }); } catch { /* never break logging */ }
      original(...args);
    };
  }

  window.addEventListener('error', (e) => {
    push(errorLog, MAX_ERRORS, {
      at: new Date().toISOString(),
      message: clip(e.message || 'Script error'),
      source: e.filename ? `${pathOnly(e.filename)}:${e.lineno}` : undefined,
      stack: e.error instanceof Error && e.error.stack ? clip(e.error.stack) : undefined,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason as unknown;
    push(errorLog, MAX_ERRORS, {
      at: new Date().toISOString(),
      message: clip(`Unhandled promise rejection: ${stringify(r)}`),
      stack: r instanceof Error && r.stack ? clip(r.stack) : undefined,
    });
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    try {
      const res = await originalFetch(input, init);
      push(networkLog, MAX_NETWORK, {
        at: new Date().toISOString(), method, path: clip(pathOnly(url)), status: res.status,
        ms: Math.round(performance.now() - started),
        requestId: res.headers.get('x-request-id') ?? res.headers.get('x-correlation-id') ?? undefined,
      });
      return res;
    } catch (err) {
      push(networkLog, MAX_NETWORK, {
        at: new Date().toISOString(), method, path: clip(pathOnly(url)), status: null,
        ms: Math.round(performance.now() - started), failed: clip(stringify(err)),
      });
      throw err;
    }
  };
}

/** A copy of the buffers as they stand right now. */
export function readSupportCapture(): { console: ConsoleEntry[]; errors: ErrorEntry[]; network: NetworkEntry[] } {
  return { console: [...consoleLog], errors: [...errorLog], network: [...networkLog] };
}
